import { UsersThree } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { AuthTeamMembership } from "~/api/governanceTypes";
import {
  useAuthMyTeams,
  useAuthTeamMembers,
  useCompleteDocumentTask,
  useCurrentWorkspace,
  useDocumentTasks,
  useUpdateAuthTeam,
} from "~/api/queries";
import { ABILITY } from "~/api/types";
import { Row, Rows } from "~/features/automation/components";
import { Button } from "~/foundation/components/Button";
import { Empty } from "~/foundation/components/Empty";
import { Input } from "~/foundation/components/Input";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { formatRelativeTime } from "~/foundation/utils/time";

function TeamCard({ team, canRename }: { team: AuthTeamMembership; canRename: boolean }) {
  const { t } = useTranslation();
  const { data: members } = useAuthTeamMembers(team.teamId);
  const updateTeam = useUpdateAuthTeam();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(team.name ?? "");

  return (
    <div className="flex flex-col gap-2 rounded-md border border-divider p-3">
      <div className="flex items-center justify-between gap-2">
        {renaming && canRename ? (
          <div className="flex flex-1 items-center gap-2">
            <Input fullWidth value={name} onChange={(event) => setName(event.target.value)} />
            <Button
              size="sm"
              variant="primary"
              disabled={!name.trim() || updateTeam.isPending}
              onClick={() =>
                updateTeam.mutate(
                  { teamId: team.teamId, name: name.trim(), slug: team.slug ?? name.trim() },
                  {
                    onSuccess: () => {
                      setRenaming(false);
                      toast.success(t("Team renamed"));
                    },
                  },
                )
              }
            >
              {t("Save")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setRenaming(false)}>
              {t("Cancel")}
            </Button>
          </div>
        ) : (
          <>
            <div className="min-w-0">
              <p className="truncate text-body font-medium text-text">{team.name ?? team.teamId}</p>
              <p className="text-small text-text-tertiary">{team.role}</p>
            </div>
            {canRename ? (
              <Button size="sm" variant="ghost" onClick={() => setRenaming(true)}>
                {t("Edit")}
              </Button>
            ) : null}
          </>
        )}
      </div>

      {members?.length ? (
        <ul className="flex flex-col gap-1">
          {members.map((member) => (
            <li key={member.userId} className="text-small text-text-secondary">
              {member.name ?? member.userId}
              {member.role === "admin" ? ` · ${t("Admin")}` : ""}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** My Team — the memberships block (§A.2). Rename is Admin-only (§E-5); everyone else reads. */
function MyTeamBlock() {
  const { t } = useTranslation();
  const { data: teams, isLoading } = useAuthMyTeams();
  const { data: workspace } = useCurrentWorkspace();
  const canAdminister = workspace?.abilities[ABILITY.workspaceUpdate] === true;

  if (isLoading) {
    return <Spinner label={t("Loading")} />;
  }
  if (!teams?.length) {
    return <Empty icon={UsersThree} message={t("You are not on any Docit.Auth team yet.")} />;
  }

  return (
    <div className="flex flex-col gap-3">
      {teams.map((team) => (
        <TeamCard key={team.teamId} team={team} canRename={canAdminister} />
      ))}
    </div>
  );
}

/** My Tasks — the assigned-work block (§A.2 A26-28). Drives `document-tasks.list`, NOT `tasks.list`. */
function MyTasksBlock() {
  const { t, i18n } = useTranslation();
  const { data: tasks, isLoading } = useDocumentTasks({ assignedToMe: true });
  const complete = useCompleteDocumentTask();

  if (isLoading) {
    return <Spinner label={t("Loading")} />;
  }
  if (!tasks?.length) {
    return <Empty message={t("Nothing assigned to you.")} />;
  }

  return (
    <Rows>
      {tasks.map((task) => {
        const overdue = task.dueDate !== null && new Date(task.dueDate).getTime() < Date.now();
        return (
          <Row key={task.id}>
            <input
              type="checkbox"
              checked={task.status === "done"}
              onChange={() => complete.mutate({ id: task.id, done: task.status !== "done" })}
              aria-label={t("Mark done")}
              className="shrink-0 cursor-pointer accent-accent"
            />
            <Link
              to="/documents/$documentId"
              params={{ documentId: task.documentId }}
              className="min-w-0 flex-1 truncate text-body text-text hover:text-accent"
            >
              {task.title}
            </Link>
            {task.dueDate ? (
              <span
                className={overdue ? "text-small text-danger" : "text-small text-text-tertiary"}
              >
                {overdue ? t("Overdue") : formatRelativeTime(task.dueDate, i18n.language)}
              </span>
            ) : null}
          </Row>
        );
      })}
    </Rows>
  );
}

export function MyWork() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <Text variant="small" className="font-medium text-text-secondary">
          {t("My team")}
        </Text>
        <MyTeamBlock />
      </div>
      <div className="flex flex-col gap-2">
        <Text variant="small" className="font-medium text-text-secondary">
          {t("My tasks")}
        </Text>
        <MyTasksBlock />
      </div>
    </div>
  );
}
